/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-nested-ternary */
/* eslint-disable react/button-has-type */
import {
  faCheck,
  faClock,
  faCopy,
  faEdit,
  faNoteSticky,
  faRotate,
  faTrash,
  faTriangleExclamation,
  faX,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  BadgeIcon,
  Button,
  Input,
  LoadingBlock,
  Modal,
  Table,
  TableColumnHeader,
  TableColumnInstance,
} from '@orobix/orobix-fe-components'
import { AxiosError } from 'axios'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'react-infinite-scroller'
import { toast } from 'react-toastify'

import DeleteConfirmationModal from '../../components/DeleteConfirmationModal/DeleteConfirmationModal'
import InfiniteListWrapper from '../../components/InfiniteListWrapper/InfiniteListWrapper'
import { SortButton } from '../../components/SortButton/SortButton'
import { endpoints } from '../../store/storeActions'
import { Sorting, SortingDirection } from '../../types/sorting'
import { del, get, post } from '../../utils/api'
import { handleApiErrors } from '../../utils/apiErrors'
import { serializeSorting } from '../../utils/sorting'
import useStyles from './DatasetsList.styles'

interface StatusCounters {
  completed: number
  running: number
  error: number
  canceled: number
  idle: number
}

export interface Dataset {
  id: number
  name: string
  notes?: string
  creationDate: string
  updateDate: string
  observations?: number[] // Array of observation IDs
  observationsCount?: number
  counterTraining?: StatusCounters // Training counts by status
  counterTrainingTest?: StatusCounters // Test counts by status
}

interface DatasetListProps {
  selectedDatasetId?: number
  productId: number
  projectId: number
  refreshTrigger?: number
  notesUpdate?: { datasetId: number; notes: string }
  onEditDataset?: (dataset: Dataset) => void
  onRowClick?: (dataset: Dataset) => void
  onLoadingChange?: (isLoading: boolean) => void
}

const itemsPerPage = 20

interface PaginatedResponse<T> {
  count: number
  next: string | null
  previous: string | null
  results: T[]
}

interface DatasetColumns extends Dataset {
  actions?: string
}

const DatasetsList = ({
  selectedDatasetId,
  productId,
  projectId,
  refreshTrigger,
  notesUpdate,
  onEditDataset,
  onRowClick,
  onLoadingChange,
}: DatasetListProps) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const [datasets, setDatasets] = useState<Dataset[]>([])
  const [selectedDataset, setSelectedDataset] = useState<DatasetColumns>()
  const [isDeleteSubmitting, setDeleteSubmitting] = useState(false)
  const [deleteConfirmationModal, setDeleteConfirmationModal] = useState(false)
  const [cloneDialog, setCloneDialog] = useState(false)
  const [cloneDataset, setCloneDataset] = useState<Dataset | null>(null)
  const [cloneName, setCloneName] = useState('')
  const [cloneError, setCloneError] = useState('')
  const [isCloneSubmitting, setIsCloneSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [totalCount, setTotalCount] = useState(0)
  const [currentPage, setCurrentPage] = useState(-1)
  const [nameInput, setNameInput] = useState('')
  const [nameFilter, setNameFilter] = useState('')
  const [sorting, setSorting] = useState<Sorting>({})
  const nameFilterRef = useRef('')
  const currentPageRef = useRef(-1)
  const sortingRef = useRef<Sorting>({})
  const filterDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleLoadMore = useCallback(
    (page: number) => {
      console.log('🔄 handleLoadMore called:', {
        page,
        currentPage: currentPageRef.current,
        isLoading,
        datasetsLength: datasets.length,
        totalCount,
      })
      if (isLoading || page <= currentPageRef.current) {
        console.log('❌ Early return:', {
          isLoading,
          page,
          currentPage: currentPageRef.current,
        })
        return
      }

      setIsLoading(true)

      const filterParam =
        nameFilterRef.current.length >= 1
          ? `&name=${encodeURIComponent(nameFilterRef.current)}`
          : ''

      const orderingParam = serializeSorting(sortingRef.current)
        ? `&ordering=${serializeSorting(sortingRef.current)}`
        : ''

      get<PaginatedResponse<Dataset>>(
        `/dataset/?product=${productId}&limit=${itemsPerPage}&offset=${
          page * itemsPerPage
        }${filterParam}${orderingParam}`
      )
        .then(async (response) => {
          console.log('✅ API response:', {
            page,
            resultsCount: response.results.length,
            totalCount: response.count,
          })
          // Calculate observation counts directly from the observations array
          const datasetsWithCounts = response.results.map((dataset) => {
            const observationsCount = Array.isArray(dataset.observations)
              ? dataset.observations.length
              : 0
            return { ...dataset, observationsCount }
          })

          if (page === 0) {
            setDatasets(datasetsWithCounts)
          } else {
            setDatasets((prev) => {
              // Prevent duplicates by checking if dataset already exists
              const existingIds = new Set(prev.map((d) => d.id))
              const newDatasets = datasetsWithCounts.filter(
                (d) => !existingIds.has(d.id)
              )
              console.log('📈 Adding new datasets:', {
                prevLength: prev.length,
                newDatasetsLength: newDatasets.length,
              })
              return [...prev, ...newDatasets]
            })
          }

          setTotalCount(response.count)
          currentPageRef.current = page
          setCurrentPage(page)
          setIsLoading(false)
          console.log('✅ Updated state:', {
            newCurrentPage: page,
            newTotalCount: response.count,
          })
        })
        // eslint-disable-next-line @typescript-eslint/no-shadow
        .catch((error) => {
          console.error('Error loading datasets:', error)
          setIsLoading(false)
          // Set empty array on error to prevent crashes
          if (page === 0) {
            setDatasets([])
          }
        })
    },
    [isLoading, productId, datasets.length, totalCount]
  )

  const handleDeleteDataset = useCallback(
    (datasetId: number) => {
      setDeleteSubmitting(true)

      del(endpoints.deleteDataset(datasetId))
        .then(() => {
          // Remove the deleted dataset from the local state
          setDatasets((prev) => prev.filter((d) => d.id !== datasetId))
          toast.success(t('Dataset successfully deleted'))
        })
        .catch((errors: AxiosError) => {
          handleApiErrors(errors)
        })
        .finally(() => {
          setDeleteSubmitting(false)
          setDeleteConfirmationModal(false)
        })
    },
    [t]
  )

  const handleCloneDataset = useCallback(async () => {
    if (!cloneDataset || !cloneName.trim()) {
      setCloneError(t('Dataset name is required'))
      return
    }

    setIsCloneSubmitting(true)
    setCloneError('')

    try {
      // Check if dataset name already exists
      const existingDatasets = await get(`/dataset/?product=${productId}`)
      const nameExists = (existingDatasets as {
        results: Dataset[]
      }).results.some(
        (dataset: Dataset) =>
          dataset.name.toLowerCase() === cloneName.trim().toLowerCase()
      )

      if (nameExists) {
        setCloneError(t('A dataset with this name already exists'))
        return
      }

      // Clone the dataset
      await post(endpoints.createDataset, {
        name: cloneName.trim(),
        product_id: productId,
        observations_ids: cloneDataset.observations || [],
      })

      toast.success(t('Dataset cloned successfully'))
      setCloneDialog(false)
      setCloneDataset(null)
      setCloneName('')
      setCloneError('')

      // Refresh the list completely by reloading data
      setCurrentPage(-1)
      setDatasets([])
      setTotalCount(0)
      setIsLoading(true)

      // Direct API call to refresh the list
      get<PaginatedResponse<Dataset>>(
        `/dataset/?product=${productId}&limit=${itemsPerPage}&offset=0`
      )
        .then(async (response) => {
          const datasetsWithCounts = response.results.map((dataset) => {
            const observationsCount = Array.isArray(dataset.observations)
              ? dataset.observations.length
              : 0
            return { ...dataset, observationsCount }
          })
          setDatasets(datasetsWithCounts)
          setTotalCount(response.count)
          currentPageRef.current = 0
          setCurrentPage(0)
        })
        .catch((error) => {
          console.error('Error refreshing dataset list:', error)
          handleApiErrors(error as AxiosError)
        })
        .finally(() => {
          setIsLoading(false)
        })
    } catch (error) {
      console.error('Error cloning dataset:', error)
      handleApiErrors(error as AxiosError)
    } finally {
      setIsCloneSubmitting(false)
    }
  }, [cloneDataset, cloneName, productId, t])

  const renderStatusBadge = (statusCounters?: StatusCounters) => {
    if (!statusCounters) return '-'

    const hasAny =
      statusCounters.idle !== 0 ||
      statusCounters.completed !== 0 ||
      statusCounters.running !== 0 ||
      statusCounters.error !== 0 ||
      statusCounters.canceled !== 0

    if (!hasAny) return ''

    return (
      <>
        {statusCounters.idle !== 0 && (
          <BadgeIcon
            icon={<FontAwesomeIcon icon={faClock} />}
            title={t('Queued')}
            badge={statusCounters.idle.toString()}
            size="xs"
          />
        )}
        {statusCounters.completed !== 0 && (
          <BadgeIcon
            icon={<FontAwesomeIcon icon={faCheck} />}
            title={t('Completed')}
            badge={statusCounters.completed.toString()}
            size="xs"
          />
        )}
        {statusCounters.running !== 0 && (
          <BadgeIcon
            icon={<FontAwesomeIcon icon={faRotate} />}
            title={t('Running')}
            badge={statusCounters.running.toString()}
            size="xs"
          />
        )}
        {statusCounters.error !== 0 && (
          <BadgeIcon
            icon={<FontAwesomeIcon icon={faTriangleExclamation} />}
            title={t('Error')}
            badge={statusCounters.error.toString()}
            size="xs"
          />
        )}
        {statusCounters.canceled !== 0 && (
          <BadgeIcon
            icon={<FontAwesomeIcon icon={faX} />}
            title={t('Aborted')}
            badge={statusCounters.canceled.toString()}
            size="xs"
          />
        )}
      </>
    )
  }

  const columns: TableColumnInstance<DatasetColumns, keyof DatasetColumns>[] = [
    {
      id: 'id',
      header: t('Id'),
      template: ({ id }) => `#${id}`,
    },
    {
      id: 'name',
      header: (
        <div className={classes.sortableColumn}>
          <TableColumnHeader
            title={t('Name')}
            sorting={
              <SortButton
                sort={sorting?.name}
                onSort={(direction) => handleSort('name', direction)}
              />
            }
          />
        </div>
      ),
      template: useCallback(
        ({ name, notes }) => (
          <>
            {name}{' '}
            {notes && <FontAwesomeIcon icon={faNoteSticky} title={notes} />}
          </>
        ),
        []
      ),
    },
    {
      id: 'creationDate',
      header: (
        <div className={classes.sortableColumn}>
          <TableColumnHeader
            title={t('Created on')}
            sorting={
              <SortButton
                sort={sorting?.creationDate}
                onSort={(direction) => handleSort('creationDate', direction)}
              />
            }
          />
        </div>
      ),
      template: ({ creationDate }) => {
        try {
          return new Date(creationDate).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          })
        } catch (err) {
          return creationDate || '-'
        }
      },
    },
    {
      id: 'updateDate',
      header: (
        <div className={classes.sortableColumn}>
          <TableColumnHeader
            title={t('Updated on')}
            sorting={
              <SortButton
                sort={sorting?.updateDate}
                onSort={(direction) => handleSort('updateDate', direction)}
              />
            }
          />
        </div>
      ),
      template: ({ updateDate }) => {
        try {
          return new Date(updateDate).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          })
        } catch (err) {
          return updateDate || '-'
        }
      },
    },
    {
      id: 'observationsCount',
      header: (
        <div className={classes.sortableColumn}>
          <TableColumnHeader
            title={t('Images')}
            sorting={
              <SortButton
                sort={sorting?.observationsCount}
                onSort={(direction) =>
                  handleSort('observationsCount', direction)
                }
              />
            }
          />
        </div>
      ),
      template: ({ observationsCount }) => observationsCount || 0,
    },
    {
      id: 'counterTraining',
      header: t('Trainings'),
      template: ({ counterTraining }) => renderStatusBadge(counterTraining),
    },
    {
      id: 'counterTrainingTest',
      header: t('Tests'),
      template: ({ counterTrainingTest }) =>
        renderStatusBadge(counterTrainingTest),
    },
    {
      id: 'actions',
      header: t('Actions'),
      noWrap: true,
      align: 'right',
      width: 0,
      template: useCallback(
        (dataset) => (
          <>
            <Button
              onClick={(e) => {
                e.stopPropagation()
                setCloneDataset(dataset)
                setCloneName(`${dataset.name} (clone)`)
                setCloneError('')
                setCloneDialog(true)
              }}
              label={t('Clone')}
              hideLabel
              title={t('Clone dataset')}
              icon={<FontAwesomeIcon icon={faCopy} />}
              buttonType="button"
              type="blank-default"
              disabled={isCloneSubmitting}
            />

            <Button
              onClick={(e) => {
                e.stopPropagation()
                if (onEditDataset) {
                  onEditDataset(dataset)
                }
              }}
              label={t('Edit')}
              hideLabel
              title={t('Edit')}
              icon={<FontAwesomeIcon icon={faEdit} />}
              buttonType="button"
              type="blank-default"
            />

            <Button
              onClick={(e) => {
                e.stopPropagation()
                setSelectedDataset(dataset)
                setDeleteConfirmationModal(true)
              }}
              label={t('Delete')}
              hideLabel
              title={t('Delete')}
              icon={<FontAwesomeIcon icon={faTrash} />}
              disabled={isDeleteSubmitting}
              buttonType="button"
              type="blank-destroy"
              size="sm"
            />
          </>
        ),
        [t, isDeleteSubmitting, isCloneSubmitting, onEditDataset]
      ),
    },
  ]

  const handleSort = (key: string, direction: SortingDirection) => {
    const newSorting = { ...sortingRef.current, [key]: direction }
    sortingRef.current = newSorting
    setSorting(newSorting)
  }

  // Reload when sorting changes
  useEffect(() => {
    currentPageRef.current = -1
    setCurrentPage(-1)
    setDatasets([])
    setTotalCount(0)
    handleLoadMore(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorting])

  // Reload when name filter changes
  useEffect(() => {
    currentPageRef.current = -1
    setCurrentPage(-1)
    setDatasets([])
    setTotalCount(0)
    handleLoadMore(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameFilter])

  // Load initial data
  useEffect(() => {
    currentPageRef.current = -1
    setCurrentPage(-1)
    handleLoadMore(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId])

  // Refresh when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger !== undefined) {
      currentPageRef.current = -1
      setCurrentPage(-1)
      handleLoadMore(0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger])

  // Notify parent component when loading state changes
  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isLoading)
    }
  }, [isLoading, onLoadingChange])

  // Update notes for a specific dataset without reloading the whole list
  useEffect(() => {
    if (notesUpdate) {
      setDatasets((prev) =>
        prev.map((d) =>
          d.id === notesUpdate.datasetId
            ? { ...d, notes: notesUpdate.notes }
            : d
        )
      )
    }
  }, [notesUpdate])

  // Listen for observation deletion events to refresh dataset counts
  useEffect(() => {
    const handleObservationsDeleted = () => {
      // Reset and reload the dataset list to get updated image counts
      currentPageRef.current = -1
      setCurrentPage(-1)
      setDatasets([])
      handleLoadMore(0)
    }

    window.addEventListener('observations-deleted', handleObservationsDeleted)

    return () => {
      window.removeEventListener(
        'observations-deleted',
        handleObservationsDeleted
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId])

  const handleNameInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    setNameInput(value)
    if (filterDebounceRef.current) {
      clearTimeout(filterDebounceRef.current)
    }
    filterDebounceRef.current = setTimeout(() => {
      if (value.length >= 1) {
        nameFilterRef.current = value
        setNameFilter(value)
      } else if (nameFilterRef.current !== '') {
        nameFilterRef.current = ''
        setNameFilter('')
      }
    }, 500)
  }

  const hasAnyCount = (counters?: StatusCounters) =>
    !!counters &&
    (counters.completed > 0 ||
      counters.running > 0 ||
      counters.error > 0 ||
      counters.canceled > 0 ||
      counters.idle > 0)

  const datasetUsedInTraining = hasAnyCount(selectedDataset?.counterTraining)
  const datasetUsedInTest = hasAnyCount(selectedDataset?.counterTrainingTest)

  const deleteConfirmationMessage = (
    <>
      <p>
        {t(
          'Are you sure you want to delete this dataset? This action cannot be undone.'
        )}
      </p>
      {(datasetUsedInTraining || datasetUsedInTest) && (
        <p>
          {t(
            'This dataset has been used in trainings and/or tests. Deleting it will cause loss of traceability for those trainings and tests.'
          )}
        </p>
      )}
    </>
  )

  return (
    <>
      <div className={classes.filterContainer}>
        <Input
          name="dataset-name-filter"
          id="dataset-name-filter"
          label=""
          placeholder={t('Search by name...')}
          type="text"
          value={nameInput}
          onChange={handleNameInputChange}
        />
      </div>
      <InfiniteListWrapper>
        {({ stickedHeader }) => (
          <InfiniteScroll
            pageStart={0}
            loadMore={handleLoadMore}
            hasMore={(() => {
              const hasMore = datasets.length < totalCount && !isLoading
              console.log('📊 hasMore check:', {
                datasetsLength: datasets.length,
                totalCount,
                isLoading,
                hasMore,
              })
              return hasMore
            })()}
            useWindow={false}
          >
            <div id="datasetsListId">
              <Table<DatasetColumns, keyof DatasetColumns>
                columns={columns}
                data={datasets}
                alternateRows
                stickyHeader
                stickedHeader={stickedHeader}
                isEmpty={datasets.length === 0 && !isLoading}
                emptyMessage={t(
                  `There's nothing here. Try creating a new {{ itemName }}.`,
                  { itemName: t('dataset') }
                )}
                selectedRow={
                  selectedDatasetId != null
                    ? datasets.findIndex((d) => d.id === selectedDatasetId)
                    : -1
                }
                onRowClick={onRowClick}
              />
              {isLoading && (
                <LoadingBlock message={t('Loading')} key="loading" />
              )}
              {!isLoading &&
                datasets.length >= totalCount &&
                totalCount > 0 && (
                  <div className={classes.endOfList}>
                    {t('All {{count}} records loaded', { count: totalCount })}
                  </div>
                )}
            </div>
          </InfiniteScroll>
        )}
      </InfiniteListWrapper>

      {deleteConfirmationModal && selectedDataset && (
        <DeleteConfirmationModal
          onCloseRequest={() => setDeleteConfirmationModal(false)}
          onDeleteRequest={() => handleDeleteDataset(selectedDataset.id)}
          message={deleteConfirmationMessage}
        />
      )}

      {cloneDialog && cloneDataset && (
        <Modal
          size="sm"
          onCloseRequest={() => {
            setCloneDialog(false)
            setCloneDataset(null)
            setCloneName('')
            setCloneError('')
          }}
          title={t('Clone Dataset')}
          footer={
            <div className={classes.cloneDialogFooter}>
              <Button
                onClick={() => {
                  setCloneDialog(false)
                  setCloneDataset(null)
                  setCloneName('')
                  setCloneError('')
                }}
                label={t('Cancel')}
                buttonType="button"
                type="default"
                disabled={isCloneSubmitting}
              />
              <Button
                onClick={handleCloneDataset}
                label={t('Clone')}
                buttonType="button"
                type="action"
                loading={isCloneSubmitting}
                disabled={!cloneName.trim()}
              />
            </div>
          }
        >
          <div className={classes.cloneDialogContent}>
            <div className={classes.cloneFormContainer}>
              <div className={classes.cloneFormLabel}>
                {t('New dataset name')} *
              </div>
              <input
                id="clone-name"
                type="text"
                value={cloneName}
                onChange={(e) => {
                  setCloneName(e.target.value)
                  setCloneError('')
                }}
                placeholder={t('Enter dataset name')}
                className={classes.cloneFormInput}
              />
              {cloneError && (
                <div className={classes.cloneFormError}>{cloneError}</div>
              )}
            </div>
            <div className={classes.cloneFormDescription}>
              {t('This will create a copy of "{{name}}" with all its images.', {
                name: cloneDataset.name,
              })}
            </div>
          </div>
        </Modal>
      )}
    </>
  )
}

export default DatasetsList
